import { Button, Header, Input, Spinner } from 'view/components';
import { PublicHeader } from 'view/components/PublicHeader';
import { FormContainer } from 'react-hook-form-mui';
import { SearchIcon, FilterIcon, CloseIcon } from 'assets';
import { useMediaQuery } from '@mui/material';
import { theme } from 'view/themes/theme';
import { useAuth } from 'view/pages/auth/hooks/useAuth';
import { yearsOfExperienceOptions } from 'view/pages/candidates/components/FiltersBlock';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as CS from 'view/modals/styled';
import { useContextSelector as useCS } from 'use-context-selector';
import { TUpdate } from 'domain/types';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { JobPostsListBlock, JobPostsListFiltersBlock } from 'view/pages/profile/components';
import { useGetJobPostsList } from 'view/pages/profile/hooks/useGetJobPostsList';
import { useGetPublicJobPostsList } from 'view/hooks/useGetPublicJobPostsList';
import { useNavigate } from 'react-router';
import { useGetDropdownData } from 'view/pages/profile/hooks/useGetDropdownData';
import { DropdownsEnum } from 'infrastructure/enums/DropdownsEnum';
import * as S from './styled';

const JobPostsListPage = () => {
  const navigate = useNavigate();
  const matches = useMediaQuery(theme.breakpoints.down('laptop'));
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const query: any = params.get('query');
  // Restore the list page from the URL so returning from a job (Back) keeps the position.
  const pageFromUrl = Number(params.get('page')) || 1;

  const [openDrawer, setOpenDrawer] = useState(false);
  const [page, setPage] = useState(pageFromUrl);
  const [queryAfterSubmit, setQueryAfterSubmit] = useState('');

  const globalUpdate: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  // Use different hooks based on authentication
  const { isLoading: isLoadingAuth, data: dataAuth, getJobPostsList } = useGetJobPostsList();
  const {
    isLoading: isLoadingPublic,
    data: dataPublic,
    getPublicJobPostsList,
  } = useGetPublicJobPostsList();

  const isLoading = isAuthenticated ? isLoadingAuth : isLoadingPublic;
  const data = isAuthenticated ? dataAuth : dataPublic;
  const { data: positionTypeData, isLoading: isPositionTypeDataLoading } = useGetDropdownData(
    DropdownsEnum.POSITION_TYPE
  );
  const { data: educationData, isLoading: isEducationDataLoading } = useGetDropdownData(
    DropdownsEnum.EDUCATION
  );

  const toggleDrawer = (newOpen: boolean) => {
    return () => {
      document.body.classList.toggle('no-scroll', newOpen);
      setOpenDrawer(newOpen);
    };
  };

  const hasFilters = (url) => {
    const params = new URLSearchParams(url);

    // Исключаемые ключи
    const excludedKeys = new Set(['query']);

    for (const [key, value] of params.entries()) {
      if (!excludedKeys.has(key) && value.trim() !== '') {
        return true; // Есть хотя бы один фильтр
      }
    }

    return false; // Нет фильтров
  };

  // Redirect authenticated users from public route to authenticated route
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && window.location.pathname === '/jobs') {
      const searchString = window.location.search;
      navigate(`/profile/job-list${searchString}`, { replace: true });
    }
  }, [isAuthenticated, isAuthLoading, navigate]);

  useEffect(() => {
    if (isAuthLoading) return;

    if (isAuthenticated) {
      getJobPostsList(query || '', page);
      if (globalUpdate) {
        globalUpdate({ isShowMatchScore: hasFilters(query) });
      }
    } else {
      getPublicJobPostsList(query || '', page);
    }

    setQueryAfterSubmit(query || '');

    // eslint-disable-next-line consistent-return
    return () => {
      if (globalUpdate && isAuthenticated) {
        globalUpdate({ isShowMatchScore: false });
      }
    };
  }, [isAuthenticated, isAuthLoading]);

  const getQueryString = (filters) => {
    const params = new URLSearchParams();
    const jobLocationArray: Array<string> = [];

    const appendLocationTypesArray = (key, array) => {
      if (Array.isArray(array) && array.length > 0) {
        params.append(
          key,
          array
            .map((item) => {
              return item;
            })
            .join(',')
        );
      }
    };

    const appendArray = (key, array) => {
      if (Array.isArray(array) && array.length > 0) {
        params.append(
          key,
          array
            .map((item) => {
              return item.id;
            })
            .join(',')
        );
      }
    };

    if (filters.query) params.append('query', filters.query);
    appendArray('positionTypeIds', filters.positionType);
    if (filters.jobLocation) params.append('locationId', filters.jobLocation.id);
    if (filters.onSite) jobLocationArray.push('onSite');
    if (filters.remote) jobLocationArray.push('remote');
    if (filters.hybrid) jobLocationArray.push('hybrid');
    appendLocationTypesArray('locationTypes', jobLocationArray);
    if (filters.yearsOfExperience) {
      const selectedItem: any = yearsOfExperienceOptions.filter((item) => {
        return filters.yearsOfExperience === item.value;
      })[0];

      params.append('yearsOfExperienceMin', selectedItem.yearsOfExperienceMin);
      params.append('yearsOfExperienceMax', selectedItem.yearsOfExperienceMax);
    }
    appendArray('educationLevelIds', filters.education);
    if (filters.salaryRange?.from) params.append('salaryMin', filters.salaryRange.from);
    if (filters.salaryRange?.to) params.append('salaryMax', filters.salaryRange.to);

    return params.toString();
  };

  const updateUrl = (queryString, pageNum = page) => {
    const params = new URLSearchParams(window.location.search);
    params.set('query', queryString);
    params.set('page', String(pageNum));
    navigate({
      pathname: window.location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const onSubmit = (page = 1) => {
    return (formData: any) => {
      const queryString = getQueryString(formData);
      setQueryAfterSubmit(queryString);

      if (isAuthenticated) {
        if (globalUpdate) {
          globalUpdate({ isShowMatchScore: hasFilters(queryString) });
        }
        getJobPostsList(queryString, page);
      } else {
        getPublicJobPostsList(queryString, page);
      }

      updateUrl(queryString, page);
      toggleDrawer(false)();
    };
  };

  const paramsFromQuery = useMemo(() => {
    return new URLSearchParams(`?${decodeURIComponent(query)}`);
  }, [query]);

  const getPositionTypesDefValue = useCallback(
    (positionTypeIds) => {
      if (!positionTypeIds || !positionTypeData) return null;
      const selectedIds = positionTypeIds?.split(',');
      const filteredItems = positionTypeData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [positionTypeData]
  );

  const getLocationTypesDefValue = useCallback((locationTypes) => {
    if (!locationTypes) return null;
    const selectedTypes = locationTypes?.split(',');
    return selectedTypes.reduce((acc, type) => {
      acc[type] = !!selectedTypes.includes(type);
      return acc;
    }, {});
  }, []);

  const getYearsOfExperienceDefValue = useCallback((yearsOfExperienceMin, yearsOfExperienceMax) => {
    if (!yearsOfExperienceMin || !yearsOfExperienceMax) return '';
    const filteredItems = yearsOfExperienceOptions.filter((item) => {
      return (
        item.yearsOfExperienceMin === +yearsOfExperienceMin &&
        item.yearsOfExperienceMax === +yearsOfExperienceMax
      );
    });
    return filteredItems?.[0]?.value || '';
  }, []);

  const getEducationDefValue = useCallback(
    (educationLevelIds) => {
      if (!educationLevelIds || !educationData) return null;
      const selectedIds = educationLevelIds?.split(',');
      const filteredItems = educationData?.filter((item) => {
        return selectedIds.includes(item.id.toString());
      });
      return filteredItems;
    },
    [educationData]
  );

  const defaultValues = {
    query: paramsFromQuery?.get('query') || '',
    positionType: getPositionTypesDefValue(paramsFromQuery?.get('positionTypeIds')) || null,
    jobLocation: null,
    onSite: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.onSite || false,
    remote: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.remote || false,
    hybrid: getLocationTypesDefValue(paramsFromQuery?.get('locationTypes'))?.hybrid || false,
    yearsOfExperience:
      getYearsOfExperienceDefValue(
        paramsFromQuery?.get('yearsOfExperienceMin'),
        paramsFromQuery?.get('yearsOfExperienceMax')
      ) || '',
    education: getEducationDefValue(paramsFromQuery?.get('educationLevelIds')) || null,
    salaryRange: {
      from: paramsFromQuery?.get('salaryMin') || '',
      to: paramsFromQuery?.get('salaryMax') || '',
    },
  };

  if (isAuthLoading) {
    return (
      <S.Wrapper>
        <Spinner variant="outlined" style={{ position: 'absolute', top: '50%', left: '50%' }} />
      </S.Wrapper>
    );
  }

  // Show loading while redirecting authenticated users from public route
  if (isAuthenticated && window.location.pathname === '/jobs') {
    return (
      <S.Wrapper>
        <Spinner variant="outlined" style={{ position: 'absolute', top: '50%', left: '50%' }} />
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper style={{ overflowY: openDrawer ? 'hidden' : 'auto' }}>
      {isAuthenticated ? <Header /> : <PublicHeader />}
      <FormContainer
        key={JSON.stringify(defaultValues)}
        defaultValues={defaultValues}
        onSuccess={onSubmit(1)}
      >
        <S.SearchContainer>
          <Input
            name="query"
            label=""
            placeholder="Search by job titles, skills, qualifications..."
            marginbottom={0.1}
            startAdornment={<SearchIcon style={{ marginRight: '8px' }} />}
          />
          <Button
            variant="contained"
            type="submit"
            onClick={() => {
              return setPage(1);
            }}
            text={matches ? '' : 'Search'}
            endIcon={matches ? <SearchIcon /> : null}
            style={
              matches
                ? {
                    padding: '10px',
                    width: '40px',
                    minWidth: '40px',
                    height: '40px',
                    minHeight: '40px',
                  }
                : undefined
            }
          />
          {matches && (
            <Button
              variant="outlined"
              text=""
              endIcon={<FilterIcon />}
              onClick={toggleDrawer(true)}
              style={{
                padding: '10px',
                width: '40px',
                minWidth: '40px',
                height: '40px',
                minHeight: '40px',
              }}
            />
          )}
        </S.SearchContainer>
        <S.MainContentWrapper>
          {matches ? (
            <div
              style={{
                display: openDrawer ? 'initial' : 'none',
                position: 'absolute',
                top: 0,
                left: 0,
                backgroundColor: 'white',
                width: '100vw',
                height: '100vh',
                zIndex: 1000,
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  padding: '24px 24px 0 24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <p
                  style={{
                    color: '#2B163F',
                    fontWeight: 700,
                    fontSize: '24px',
                    lineHeight: '120%',
                  }}
                >
                  Filters
                </p>
                <CS.CloseIconBox onClick={toggleDrawer(false)}>
                  <CloseIcon />
                </CS.CloseIconBox>
              </div>
              <JobPostsListFiltersBlock
                onSubmit={onSubmit}
                setPage={setPage}
                positionTypeData={positionTypeData}
                isPositionTypeDataLoading={isPositionTypeDataLoading}
                educationData={educationData}
                isEducationDataLoading={isEducationDataLoading}
              />
            </div>
          ) : (
            <JobPostsListFiltersBlock
              onSubmit={onSubmit}
              setPage={setPage}
              positionTypeData={positionTypeData}
              isPositionTypeDataLoading={isPositionTypeDataLoading}
              educationData={educationData}
              isEducationDataLoading={isEducationDataLoading}
            />
          )}
          <JobPostsListBlock
            isLoading={isLoading}
            data={data}
            onSubmit={onSubmit}
            page={page}
            setPage={setPage}
            queryAfterSubmit={queryAfterSubmit}
          />
        </S.MainContentWrapper>
      </FormContainer>
    </S.Wrapper>
  );
};

export default JobPostsListPage;
